import { Bar } from 'vue-chartjs'
import themedChart from '@/components/charts/mixins/themedChart'
import { cssVar } from '@/utils/cssVars'

function solidifyColor(c) {
    if (!c) return c;
    // If rgba(..., a) and a < 1, force to 1
    if (typeof c === 'string') {
        const m = c.match(/^rgba\s*\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)\s*\)$/i);
        if (m) {
            return `rgba(${m[1]}, ${m[2]}, ${m[3]}, 1)`;
        }
        return c;
    }
    return c;
}

function buildPalette() {
    // Use theme tokens so colors adapt to light/dark without hardcoding
    return [
        cssVar('--accent-primary', '#339CFF'),
        cssVar('--accent-secondary', '#A529FF'),
        cssVar('--success', '#32D74B'),
        cssVar('--warning', '#FF9500'),
        cssVar('--danger', '#FF3B30')
    ];
}

// Strip cycle-prone properties and clone safely
function stripCycles(obj, seen = new WeakSet()) {
    if (!obj || typeof obj !== 'object') return obj;
    if (seen.has(obj)) return undefined;
    seen.add(obj);
    if (Array.isArray(obj)) return obj.map(v => stripCycles(v, seen));
    const out = {};
    const omit = new Set(['_meta','meta','chart','config','$context','_chart']);
    Object.keys(obj).forEach(k => {
        if (omit.has(k)) return;
        out[k] = stripCycles(obj[k], seen);
    });
    return out;
}
function safeCloneData(data) {
    try {
        if (typeof structuredClone === 'function') return structuredClone(data);
    } catch (e) {}
    try {
        return JSON.parse(JSON.stringify(stripCycles(data)));
    } catch (e) {
        const ds = Array.isArray(data && data.datasets) ? data.datasets.map(d => {
            const { _meta, meta, chart, config, $context, _chart, ...rest } = (d || {});
            return Object.assign({}, rest);
        }) : [];
        const labels = Array.isArray(data && data.labels) ? data.labels.slice() : [];
        return { datasets: ds, labels };
    }
}

function normalizeDatasets(data) {
    const palette = buildPalette();
    const fallback = palette[1] || '#A529FF';
    if (!data || !Array.isArray(data.datasets)) return data;
    data.datasets = data.datasets.map((ds, i) => {
        const copy = Object.assign({}, ds);
        // Ensure visible, opaque bar fills and distinct colors per metric
        if (!copy.backgroundColor) {
            copy.backgroundColor = palette[i % palette.length] || fallback;
        } else if (Array.isArray(copy.backgroundColor)) {
            copy.backgroundColor = copy.backgroundColor.map(solidifyColor);
        } else {
            copy.backgroundColor = solidifyColor(copy.backgroundColor);
        }
        // BorderColor optional, keep same tone if missing
        if (!copy.borderColor) {
            copy.borderColor = copy.backgroundColor;
        } else if (Array.isArray(copy.borderColor)) {
            copy.borderColor = copy.borderColor.map(solidifyColor);
        } else {
            copy.borderColor = solidifyColor(copy.borderColor);
        }
        return copy;
    });
    return data;
}

export default {
    extends: Bar,
    mixins: [themedChart],
    props: {
        /**
         * Data object for Chart.js. Do not mutate inside this component.
         * We clone it before rendering to avoid feedback loops.
         */
        chartData: { type: Object, required: true },
        /** Chart.js options (theming merged automatically) */
        options: { type: Object, default: () => ({ maintainAspectRatio: false }) },
        height: { default: () => 400 }
    },
    watch: {
        chartData: {
            immediate: false,
            // Deep watch so parent in-place mutations to datasets/labels are picked up
            deep: true,
            handler(newData) {
                // Clone to avoid mutating prop and breaking reactivity/causing loops
                if (!this.$refs || !this.$refs.canvas) {
                    // Defer until canvas ref exists to avoid vue-chartjs template warning
                    return;
                }
                const safe = newData ? safeCloneData(newData) : { datasets: [], labels: [] };
                this.renderChart(normalizeDatasets(safe), this.themedOptions(this.options));
            }
        },
        options: {
            deep: false,
            handler() {
                const safe = this.chartData ? safeCloneData(this.chartData) : { datasets: [], labels: [] }
                this.renderChart(normalizeDatasets(safe), this.themedOptions(this.options))
            }
        }
    },
    mounted () {
        // Initial render with cloned data and themed options
        const safe = this.chartData ? safeCloneData(this.chartData) : { datasets: [], labels: [] }
        this.renderChart(normalizeDatasets(safe), this.themedOptions(this.options))
    }
}
